import React, { useState } from 'react';
import styled from 'styled-components';
import { Modal } from '../../atoms/Modal/Modal';
import useModal from '../../../services/hooks/useModal';
import HeaderContent from '../../atoms/Layout/HeaderContent';
import CenterContent from '../../atoms/Layout/CenterContent';
import useLogTimeSpentOnPage from '../../../services/hooks/useLogTimeSpentOnPage';
import ErrorMessage from '../../atoms/Message/Error/ErrorMessage';
import { DefaultTheme, withTheme } from 'styled-components';
import AddBuyerForm from './AddBuyerForm';
import { useB2BStore } from '../../../store/b2b/b2bStore';
import { SearchForm } from './SearchForm';
import Card from '../../atoms/Card/Card';
import SpinnerWrapper from '../../atoms/Spinner/SpinnerWrapper';
import Spinner from '../../atoms/Spinner/Spinner';
import Table from '../../atoms/Table/Table';
import { ColumnDef } from '../../atoms/Table/tableFeatures';
import { Organization } from '../../../types/b2b/Organization';
import TwoRowCell from '../../atoms/Table/TwoRowCell';
import { ErrorType } from '../../../types/response/ErrorCodes';
import CardBody from '../../atoms/Card/CardBody';
import InformationMessage from '../../atoms/Message/Information/InformationMessage';
import Term from '../../atoms/Term/Term';
import TermCategory from '../../../types/filter/TermCategory';
import { useAppState } from '../../../store/appstate';
import { ConnectedStore } from '../../../types/ConnectedStore';
import { useNavigate } from 'react-router-dom';
import { getStoresAvailableForUser } from '../../../services/helpers/storeHelper';
import { SessionState } from '../../../store/session/sessionSlice';

interface B2BManagementProps {
    theme: DefaultTheme;
}

const OrganizationListColumns: ColumnDef<Organization>[] = [
    {
        header: 'Organization',
        accessorKey: 'name',
        cell: (props: { row: { original: Organization } }) => {
            return <TwoRowCell upper={props.row.original.name} lower={props.row.original.id} />;
        },
    },
];

const B2BManagement: React.FC<B2BManagementProps> = ({ theme }: B2BManagementProps) => {
    useLogTimeSpentOnPage('b2b');
    const addBuyerModal = useModal();

    const { reset, searchResult, isSearching, isFailed, isCanceled, error } = useB2BStore();

    const organizations = searchResult?.data ?? [];
    const [hasSearched, setHasSearched] = useState(false);

    const onHideAddBuyerModal = () => {
        reset();
        addBuyerModal.toggle();
    };

    const navigate = useNavigate();

    const { user } = useAppState<SessionState>(s => s.session);

    let availableStores = useAppState<ConnectedStore[]>(s => s.session.availableStores) ?? [];

    availableStores = getStoresAvailableForUser(availableStores, user?.filteredStores);

    const countryCodes = Array.from(new Set(availableStores.map(x => x.countryCode))).filter(
        code => code === 'SE' || code === 'NO' || code === 'FI'
    );

    const countryTerm = {
        key: 'country',
        values: countryCodes.map(code => ({ value: code || '', count: 0 })),
    };

    const [countryCode, setCountryCode] = useState<string>(() => {
        const firstCountry = countryTerm.values[0]?.value || availableStores[0]?.countryCode || '';
        return firstCountry;
    });

    const handleCountryChange = (termKey: string, selectedFilters: string[]) => {
        if (termKey === TermCategory.Country && selectedFilters.length > 0) {
            setCountryCode(selectedFilters[0]);
        }
    };

    const onRowClick = (row: Organization) => {
        navigate(`/customers/by/orgId/${countryCode}/${row.id}`);
    };

    return (
        <>
            <HeaderContent>
                <CenterContent>
                    <SearchForm countryCode={countryCode} setHasSearched={setHasSearched} />
                    <Terms>
                        <Term
                            category={TermCategory.Country}
                            term={countryTerm}
                            currentFilters={[countryCode]}
                            onTermFilterChange={handleCountryChange}
                        />
                    </Terms>
                </CenterContent>
            </HeaderContent>

            <Modal
                mediumLarge
                isShown={addBuyerModal.isShown}
                hide={addBuyerModal.toggle}
                verticalCenter={true}
            >
                <AddBuyerForm hide={onHideAddBuyerModal} />
            </Modal>

            <CenterContent>
                <Container>
                    <Card>
                        <CardBody>
                            {isFailed ? (
                                <MessageWrapper>
                                    <ErrorMessage error={error} errorHeader={ErrorType.Search} />
                                </MessageWrapper>
                            ) : isSearching || isCanceled ? (
                                <SpinnerWrapper>
                                    <Spinner
                                        text="Searching..."
                                        loading={isSearching || isCanceled}
                                    />
                                </SpinnerWrapper>
                            ) : organizations && organizations.length === 0 ? (
                                <MessageWrapper>
                                    <InformationMessage
                                        messages={[
                                            hasSearched
                                                ? 'Please try a different search query.'
                                                : 'Please search for a company name or organization number',
                                        ]}
                                        messageHeader="No organizations found"
                                    />
                                </MessageWrapper>
                            ) : (
                                <TableWrapper isSearching={isSearching || isCanceled}>
                                    <Table<Organization>
                                        data={organizations ?? []}
                                        columns={OrganizationListColumns}
                                        onRowClicked={onRowClick}
                                    />
                                </TableWrapper>
                            )}
                        </CardBody>
                    </Card>
                </Container>
            </CenterContent>
        </>
    );
};

export default withTheme(B2BManagement) as React.ComponentType<Omit<B2BManagementProps, 'theme'>>;

const Container = styled.div`
    margin: 5rem 0 5rem 0;
`;

interface StyleProps {
    isSearching: boolean;
}

const TableWrapper = styled.div<StyleProps>`
    opacity: ${props => (props.isSearching ? 0.5 : 1)};
`;

const MessageWrapper = styled.div`
    margin: 2rem;
    padding-top: 1rem;
`;

const Terms = styled.div`
    display: flex;
    flex-direction: row;
    gap: 0.5rem;
`;
